/**
 * Лейблы байка: чипы, снятие (×) и выбор «+ лейбл».
 *
 * Скаут лейблы ставит и снимает, но не создаёт и не удаляет сам лейбл —
 * это справочник диспетчера. Партнёрский скаут (`readOnly`) видит чипы и
 * ничего не меняет, поэтому хуки мутаций в его ветке даже не создаются.
 *
 * Цвет лейбла — только точка у названия: фон от диспетчера произвольный, и
 * читаемость текста на нём не гарантирована.
 */
import { useState } from 'react'
import { useAttachLabel, useDetachLabel, useLabels, type Label } from '@/api/vehicles'

function LabelDot({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      className="inline-block size-2.5 shrink-0 rounded-full border border-white/30"
      style={{ backgroundColor: color || undefined }}
    />
  )
}

const chipClass =
  'inline-flex items-center gap-1.5 rounded-full border border-current/30 px-2.5 py-1 text-xs'

/** Чипы без действий: всплывашка карты и партнёрская карточка. */
export function LabelChips({ labels }: { labels: readonly Label[] }) {
  if (labels.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1">
      {labels.map((l) => (
        <span key={l.id} className={chipClass}>
          <LabelDot color={l.color} />
          {l.name}
        </span>
      ))}
    </div>
  )
}

export function BikeLabels({
  vehicleId,
  labels,
  readOnly,
}: {
  vehicleId: string
  labels: readonly Label[]
  readOnly: boolean
}) {
  if (readOnly) return <LabelChips labels={labels} />
  return <LabelEditor vehicleId={vehicleId} labels={labels} />
}

function LabelEditor({ vehicleId, labels }: { vehicleId: string; labels: readonly Label[] }) {
  const all = useLabels()
  const attach = useAttachLabel()
  const detach = useDetachLabel()
  const [picking, setPicking] = useState(false)

  const free = (all.data ?? []).filter((l) => !labels.some((x) => x.id === l.id))

  return (
    <div className="flex flex-wrap items-center gap-1">
      {labels.map((l) => (
        <span key={l.id} className={chipClass}>
          <LabelDot color={l.color} />
          {l.name}
          <button
            aria-label={`Снять лейбл ${l.name}`}
            className="-my-2 -mr-2 min-h-10 min-w-10 rounded-full opacity-70"
            onClick={() => {
              detach.mutate({ vehicleId, labelId: l.id })
            }}
          >
            ×
          </button>
        </span>
      ))}
      <button
        aria-expanded={picking}
        onClick={() => {
          setPicking(!picking)
        }}
        className={`${chipClass} border-dashed`}
      >
        + лейбл
      </button>
      {picking ? (
        free.length === 0 && all.isSuccess ? (
          <span className="text-xs opacity-60">Больше лейблов нет</span>
        ) : (
          free.map((l) => (
            <button
              key={l.id}
              onClick={() => {
                attach.mutate({ vehicleId, label: l })
                setPicking(false)
              }}
              className={chipClass}
            >
              <LabelDot color={l.color} />
              {l.name}
            </button>
          ))
        )
      ) : null}
      {attach.isError || detach.isError ? (
        <span className="text-xs text-red-400">Не удалось сохранить лейбл</span>
      ) : null}
    </div>
  )
}
